import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, X } from 'lucide-react';
import { categoriesApi } from '../services/api';
import './ProductCategoryModal.css';

const ProductCategoryModal = ({ category, onClose, onSave }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    default_price: '',
    currency: '₪',
    icon: '📁',
    is_active: true,
    update_product_prices: true, // Checkbox for updating existing products
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Common icons for categories
  const commonIcons = ['📁', '👕', '👟', '📱', '💻', '🎮', '📚', '🍔', '☕', '🎨', '🏠', '⚽', '🎵', '💄', '🔧', '🚗'];

  useEffect(() => {
    if (category) {
      setFormData({
        name: category.name || '',
        description: category.description || '',
        default_price: category.default_price || '',
        currency: category.currency || '₪',
        icon: category.icon || '📁',
        is_active: category.is_active !== false,
        update_product_prices: true,
      });
    }
  }, [category]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError(t('products.categoryNameRequired'));
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (category) {
        // Update existing category
        await categoriesApi.update(category.id, formData);
      } else {
        // Create new category
        await categoriesApi.create(formData);
      }
      
      onSave();
    } catch (err) {
      setError(err.message || t('products.saveError'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{category ? t('products.editCategory') : t('products.addCategory')}</h2>
          <button
            type="button"
            className="close-btn"
            onClick={onClose}
            aria-label={t('common.cancel')}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {error && (
            <div className="error-message">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {/* Icon Selector */}
          <div className="form-group">
            <label>{t('products.categoryIcon')}</label>
            <div className="icon-grid">
              {commonIcons.map((icon) => (
                <button
                  key={icon}
                  type="button"
                  className={`icon-option ${formData.icon === icon ? 'selected' : ''}`}
                  onClick={() => handleChange('icon', icon)}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>

          {/* Name */}
          <div className="form-group">
            <label>{t('products.categoryName')} *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder={t('products.categoryNamePlaceholder')}
              required
            />
          </div>

          {/* Description */}
          <div className="form-group">
            <label>{t('products.description')}</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder={t('products.descriptionPlaceholder')}
              rows="3"
            />
          </div>

          {/* Default Price */}
          <div className="form-row">
            <div className="form-group">
              <label>{t('products.defaultPrice')}</label>
              <input
                type="number"
                step="0.01"
                value={formData.default_price}
                onChange={(e) => handleChange('default_price', e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="form-group">
              <label>{t('products.currency')}</label>
              <select
                value={formData.currency}
                onChange={(e) => handleChange('currency', e.target.value)}
              >
                <option value="₪">₪ (ILS)</option>
                <option value="$">$ (USD)</option>
                <option value="€">€ (EUR)</option>
              </select>
            </div>
          </div>

          {/* Update products checkbox (only when editing and price is changed) */}
          {category && (
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.update_product_prices}
                  onChange={(e) => handleChange('update_product_prices', e.target.checked)}
                />
                <span>{t('products.updateProductPrices')}</span>
              </label>
              <p className="help-text">{t('products.updateProductPricesHelp')}</p>
            </div>
          )}

          {/* Is Active */}
          <div className="form-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => handleChange('is_active', e.target.checked)}
              />
              <span>{t('products.active')}</span>
            </label>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductCategoryModal;
